import API from '@aws-amplify/api';
import Utils from '@/common/utils/utils';
import Logger from '@/common/utils/Logger';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { LOCAL_MCP_USER_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';
import { MCP_BOOTSTRAP_ACTION_TYPE } from '@/common/constant/McpBootstrapConstant';

/**
 * MCP-003-F3 — Local MCP Bootstrap service.
 *
 * Components never call GraphQL directly; everything goes through this service.
 * Backend mutation: mcpBootstrap(mcpBootstrapRequestDto) → { status, data, errorMsg, errorCode }
 *  - data is a JSON string:
 *    + GET_STATUS: { eligible, latestVersion, latestReleaseDate, bootstrapVersion, downloadedAt,
 *                    hasUpdate, hasMcpCatalogUpdate, errorCode }
 *    + GENERATE:   { downloadUrl, bootstrapVersion, expiresAt }
 *
 * src/graphql/* is auto-generated, so the mutation is defined inline here instead.
 */
const TAG = 'mcpBootstrapService';

const mcpBootstrapMutation = /* GraphQL */ `
    mutation McpBootstrap($mcpBootstrapRequestDto: MCPBootstrapRequestDto!) {
        mcpBootstrap(mcpBootstrapRequestDto: $mcpBootstrapRequestDto) {
            status
            data
            errorMsg
            errorCode
        }
    }
`;

/**
 * Calls the mcpBootstrap mutation and normalizes the response to { success, data, errorCode }.
 * Never persists credentials/download URL to localStorage/Recoil.
 *
 * @param {string} actionType - GET_STATUS | GENERATE
 * @param {string} corporationId
 * @param {string} projectId
 * @param {string} [prefix] - LOCAL_MCP_USER_PREFIX '[U]' (default) | LOCAL_MCP_PROJECT_PREFIX '[P]'
 */
const invokeBootstrap = async (actionType, corporationId, projectId, prefix = LOCAL_MCP_USER_PREFIX) => {
    try {
        const requestDto = {
            corporationInuse: corporationId,
            projectInuse: projectId,
            userId: localStorageUtil.getUserDetailId(),
            actionType,
            scope: prefix
        };

        const res = await API.graphql({
            query: mcpBootstrapMutation,
            variables: { mcpBootstrapRequestDto: requestDto }
        });

        const response = res?.data?.mcpBootstrap;
        if (!response?.status) {
            return { success: false, errorCode: response?.errorCode || null };
        }

        return {
            success: true,
            data: Utils.convertJSONDataToObject(response.data) || {}
        };
    } catch (err) {
        // Never log the URL/credentials.
        Logger.error(TAG, 'mcpBootstrap error >>>', actionType);
        return { success: false, errorCode: null };
    }
};

/**
 * FE-01 — Fetches eligibility + version info for the 外部連携設定 screen.
 *
 * @param {string} corporationId
 * @param {string} projectId
 * @param {string} [prefix] - LOCAL_MCP_USER_PREFIX '[U]' (default) | LOCAL_MCP_PROJECT_PREFIX '[P]'
 * @returns {{ success, eligible, latestVersion, latestReleaseDate, bootstrapVersion,
 *             downloadedAt, hasUpdate, hasMcpCatalogUpdate, errorCode }}
 */
export const getMCPBootstrapStatus = async (corporationId, projectId, prefix) => {
    const result = await invokeBootstrap(
        MCP_BOOTSTRAP_ACTION_TYPE.GET_STATUS,
        corporationId,
        projectId,
        prefix
    );

    if (!result.success) {
        return { success: false, eligible: false, errorCode: result.errorCode };
    }

    const {
        eligible,
        latestVersion,
        latestReleaseDate,
        bootstrapVersion,
        downloadedAt,
        hasUpdate,
        hasMcpCatalogUpdate,
        errorCode
    } = result.data;

    return {
        success: true,
        eligible: !!eligible,
        latestVersion: latestVersion || null,
        latestReleaseDate: latestReleaseDate || null,
        bootstrapVersion: bootstrapVersion || null,
        downloadedAt: downloadedAt || null,
        hasUpdate: !!hasUpdate,
        hasMcpCatalogUpdate: !!hasMcpCatalogUpdate,
        errorCode: errorCode || null
    };
};

/**
 * FE-01 — Get-or-create the tunnel + generate the Bootstrap ZIP, return a presigned URL.
 *
 * @param {string} corporationId
 * @param {string} projectId
 * @param {string} [prefix] - LOCAL_MCP_USER_PREFIX '[U]' (default) | LOCAL_MCP_PROJECT_PREFIX '[P]'
 * @returns {{ success, downloadUrl, bootstrapVersion, expiresAt, errorCode }}
 */
export const generateMCPBootstrap = async (corporationId, projectId, prefix) => {
    const result = await invokeBootstrap(
        MCP_BOOTSTRAP_ACTION_TYPE.GENERATE,
        corporationId,
        projectId,
        prefix
    );

    if (!result.success) {
        return { success: false, errorCode: result.errorCode };
    }

    const { downloadUrl, bootstrapVersion, expiresAt } = result.data;
    return {
        success: true,
        downloadUrl: downloadUrl || null,
        bootstrapVersion: bootstrapVersion || null,
        expiresAt: expiresAt || null
    };
};

/**
 * FE-01/FE-04 — Triggers the download from the presigned URL using a hidden iframe.
 * Using iframe instead of anchor click to avoid Firefox loading state issues where the
 * browser keeps showing loading indicator even after the file has been downloaded.
 * Never logs the URL.
 */
export const downloadMCPBootstrap = (downloadUrl, fileName) => {
    if (Utils.isEmptyString(downloadUrl)) return false;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);
    setTimeout(() => {
        document.body.removeChild(iframe);
    }, 10000);
    return true;
};
